import React, { useState, useEffect, useRef, useMemo } from 'react';
import AdminBackLink from '../components/AdminBackLink';

import axiosInstance from '../services/api';
import api from '../config/api';
import { BlogPost, BlogPostMedia } from '../types';
import auth from '../utils/auth';
import RichTextEditor, { RichTextEditorRef } from '../components/RichTextEditor';
import { HiOutlinePencil, HiOutlineTrash, HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Function to generate slug from title
const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Blog: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const mediaFilesRef = useRef<File[]>([]);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [mediaDeleting, setMediaDeleting] = useState<number | null>(null);
  const [mediaSorting, setMediaSorting] = useState(false);
  const [draggedMediaId, setDraggedMediaId] = useState<number | null>(null);
  const [dragOverMediaId, setDragOverMediaId] = useState<number | null>(null);
  const [draggedPendingIndex, setDraggedPendingIndex] = useState<number | null>(null);
  const [dragOverPendingIndex, setDragOverPendingIndex] = useState<number | null>(null);
  const [filterPublished, setFilterPublished] = useState<boolean | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  // Refs for RichTextEditor components to get current values
  const contentArRef = useRef<RichTextEditorRef>(null);
  const contentEnRef = useRef<RichTextEditorRef>(null);

  const [formData, setFormData] = useState({
    titleAr: '',
    titleEn: '',
    slugAr: '',
    slugEn: '',
    excerptAr: '',
    excerptEn: '',
    contentAr: '',
    contentEn: '',
    image: null as File | null,
    readingTime: 1,
    isPublished: false,
  });

  const pendingMediaPreviews = useMemo(
    () =>
      mediaFiles.map((file, index) => ({
        index,
        file,
        url: URL.createObjectURL(file),
        isVideo: file.type.startsWith('video/'),
      })),
    [mediaFiles]
  );

  const sortedEditableMedia = useMemo(
    () =>
      ([...(editingPost?.media ?? [])] as BlogPostMedia[])
        .filter((m) => m.id !== 0)
        .sort((a, b) => a.orderIndex - b.orderIndex),
    [editingPost?.media]
  );

  useEffect(() => {
    return () => {
      pendingMediaPreviews.forEach((item) => URL.revokeObjectURL(item.url));
    };
  }, [pendingMediaPreviews]);

  useEffect(() => {
    fetchPosts();
  }, [page, filterPublished]);

  useEffect(() => {
    if (!showForm) return;
    if (!error && !success) return;
    const timeout = setTimeout(() => {
      setError('');
      setSuccess('');
    }, 3500);
    return () => clearTimeout(timeout);
  }, [showForm, error, success]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError('');
      const published = filterPublished !== undefined ? filterPublished : undefined;
      const response = await axiosInstance.get(api.blog.getAll(published, page, limit));
      setPosts(response.data.posts || []);
      setTotalPages(response.data.totalPages || 1);
      setTotal(response.data.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل تحميل المقالات');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (post: BlogPost) => {
    console.log('Blog: handleEdit called with post:', {
      id: post.id,
      contentArLength: post.contentAr ? post.contentAr.length : 0,
      contentEnLength: post.contentEn ? post.contentEn.length : 0,
      excerptArLength: post.excerptAr ? post.excerptAr.length : 0,
      excerptEnLength: post.excerptEn ? post.excerptEn.length : 0,
    });
    setEditingPost(post);
    const newFormData = {
      titleAr: post.titleAr,
      titleEn: post.titleEn,
      slugAr: post.slugAr,
      slugEn: post.slugEn,
      excerptAr: post.excerptAr || '',
      excerptEn: post.excerptEn || '',
      contentAr: post.contentAr,
      contentEn: post.contentEn,
      image: null,
      readingTime: post.readingTime,
      isPublished: post.isPublished,
    };
    console.log('Blog: Setting formData in handleEdit:', {
      contentArLength: newFormData.contentAr ? newFormData.contentAr.length : 0,
      contentEnLength: newFormData.contentEn ? newFormData.contentEn.length : 0,
      excerptArLength: newFormData.excerptAr ? newFormData.excerptAr.length : 0,
      excerptEnLength: newFormData.excerptEn ? newFormData.excerptEn.length : 0,
    });
    setFormData(newFormData);
    setImagePreview(post.image ? `${API_URL.replace('/api', '')}${post.image}` : null);
    setMediaFiles([]);
    mediaFilesRef.current = [];
    setDraggedPendingIndex(null);
    setDragOverPendingIndex(null);

    try {
      // Always get full post details to ensure media list is complete in edit mode
      const detailsRes = await axiosInstance.get(api.blog.getById(post.id));
      const fullPost = detailsRes.data as BlogPost;
      setEditingPost(fullPost);
    } catch {
      // fallback: keep list payload if details request fails
      setEditingPost(post);
    }
    setShowForm(true);
  };

  const handleTitleChange = (field: 'titleAr' | 'titleEn', value: string) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      // Auto-generate slug
      if (field === 'titleAr') {
        updated.slugAr = generateSlug(value);
      } else {
        updated.slugEn = generateSlug(value);
      }
      return updated;
    });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMediaFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files?.length) {
      const newList = [...mediaFilesRef.current, ...Array.from(files)];
      mediaFilesRef.current = newList;
      setMediaFiles(newList);
    }
    e.target.value = '';
  };

  const removePendingMedia = (index: number) => {
    const next = mediaFiles.filter((_, i) => i !== index);
    mediaFilesRef.current = next;
    setMediaFiles(next);
  };

  const reorderPendingFiles = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    setMediaFiles((prev) => {
      if (fromIndex >= prev.length || toIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      mediaFilesRef.current = next;
      return next;
    });
  };

  const reorderMediaList = (list: BlogPostMedia[], fromId: number, toId: number) => {
    const fromIndex = list.findIndex((m) => m.id === fromId);
    const toIndex = list.findIndex((m) => m.id === toId);
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return list;

    const next = [...list];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next.map((m, idx) => ({ ...m, orderIndex: idx + 1 }));
  };

  const handleMediaDrop = async (targetMediaId: number) => {
    if (!editingPost || !draggedMediaId || draggedMediaId === targetMediaId) {
      setDraggedMediaId(null);
      setDragOverMediaId(null);
      return;
    }

    const current = sortedEditableMedia;
    const reordered = reorderMediaList(current, draggedMediaId, targetMediaId);
    const isChanged =
      reordered.length === current.length &&
      reordered.some((m, idx) => m.id !== current[idx]?.id);

    setDraggedMediaId(null);
    setDragOverMediaId(null);
    if (!isChanged) return;

    const prevMedia = editingPost.media || [];
    const reorderedMap = new Map(reordered.map((m) => [m.id, m.orderIndex]));
    setEditingPost((prev) =>
      prev
        ? {
            ...prev,
            media: (prev.media || []).map((m) =>
              reorderedMap.has(m.id)
                ? { ...m, orderIndex: reorderedMap.get(m.id)! }
                : m
            ),
          }
        : null
    );

    try {
      setMediaSorting(true);
      await axiosInstance.put(api.blog.reorderMedia(editingPost.id), {
        order: reordered.map((m, idx) => ({ id: m.id, orderIndex: idx + 1 })),
      });
      setSuccess('تم حفظ ترتيب الميديا بنجاح');
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل حفظ ترتيب الميديا');
      setEditingPost((prev) => (prev ? { ...prev, media: prevMedia } : null));
    } finally {
      setMediaSorting(false);
    }
  };

  const handleUploadMediaNow = async () => {
    if (!editingPost || mediaFiles.length === 0) return;
    try {
      setMediaUploading(true);
      setError('');
      const form = new FormData();
      mediaFiles.forEach((f) => form.append('media', f));
      await axiosInstance.post(api.blog.addMedia(editingPost.id), form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const res = await axiosInstance.get(api.blog.getById(editingPost.id));
      setEditingPost((prev) => (prev ? { ...prev, media: res.data.media ?? prev.media } : null));
      mediaFilesRef.current = [];
      setMediaFiles([]);
      setSuccess('تم رفع الملفات بنجاح');
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل رفع الملفات');
    } finally {
      setMediaUploading(false);
    }
  };

  const handleDeleteMedia = async (mediaId: number) => {
    if (!editingPost) return;
    try {
      setMediaDeleting(mediaId);
      await axiosInstance.delete(api.blog.deleteMedia(editingPost.id, mediaId));
      setEditingPost((prev) =>
        prev ? { ...prev, media: (prev.media || []).filter((m) => m.id !== mediaId) } : null
      );
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل حذف الملف');
    } finally {
      setMediaDeleting(null);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المقال؟')) return;

    try {
      setError('');
      setSuccess('');
      await axiosInstance.delete(api.blog.delete(id));
      setSuccess('تم حذف المقال بنجاح');
      fetchPosts();
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل حذف المقال');
    }
  };

  const handleTogglePublished = async (post: BlogPost) => {
    try {
      setError('');
      setSuccess('');
      const formData = new FormData();
      formData.append('isPublished', String(!post.isPublished));
      
      await axiosInstance.put(api.blog.update(post.id), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setSuccess('تم تحديث حالة النشر بنجاح');
      fetchPosts();
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل تحديث حالة النشر');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');

      // Get current values directly from RichTextEditor refs first (most up-to-date)
      const currentContentAr = contentArRef.current?.getValue() || formData.contentAr || '';
      const currentContentEn = contentEnRef.current?.getValue() || formData.contentEn || '';
      
      // Also sync values to formData for consistency (but use ref values for submission)
      if (currentContentAr !== formData.contentAr) {
        setFormData(prev => ({ ...prev, contentAr: currentContentAr }));
      }
      if (currentContentEn !== formData.contentEn) {
        setFormData(prev => ({ ...prev, contentEn: currentContentEn }));
      }

      // Debug: Log formData before creating FormData
      console.log('Blog: formData before submit:', {
        contentAr: formData.contentAr ? `Length: ${formData.contentAr.length}, Preview: ${formData.contentAr.substring(0, 100)}` : 'EMPTY',
        contentEn: formData.contentEn ? `Length: ${formData.contentEn.length}, Preview: ${formData.contentEn.substring(0, 100)}` : 'EMPTY',
        excerptAr: formData.excerptAr ? `Length: ${formData.excerptAr.length}, Preview: ${formData.excerptAr.substring(0, 100)}` : 'EMPTY',
        excerptEn: formData.excerptEn ? `Length: ${formData.excerptEn.length}, Preview: ${formData.excerptEn.substring(0, 100)}` : 'EMPTY',
        titleAr: formData.titleAr,
        titleEn: formData.titleEn,
      });
      console.log('Blog: Current values from refs:', {
        contentAr: currentContentAr ? `Length: ${currentContentAr.length}, Preview: ${currentContentAr.substring(0, 100)}` : 'EMPTY',
        contentEn: currentContentEn ? `Length: ${currentContentEn.length}, Preview: ${currentContentEn.substring(0, 100)}` : 'EMPTY',
      });
      console.log('Blog: Full formData object:', JSON.stringify(formData, null, 2));

      const submitFormData = new FormData();
      submitFormData.append('titleAr', formData.titleAr || '');
      submitFormData.append('titleEn', formData.titleEn || '');
      submitFormData.append('slugAr', formData.slugAr || '');
      submitFormData.append('slugEn', formData.slugEn || '');
      submitFormData.append('excerptAr', formData.excerptAr || '');
      submitFormData.append('excerptEn', formData.excerptEn || '');
      submitFormData.append('contentAr', currentContentAr);
      submitFormData.append('contentEn', currentContentEn);
      submitFormData.append('readingTime', String(formData.readingTime));
      submitFormData.append('isPublished', String(formData.isPublished));

      if (formData.image) {
        submitFormData.append('image', formData.image);
      }

      // Debug: Verify FormData values
      console.log('Blog: FormData values:', {
        contentAr: submitFormData.get('contentAr') ? `Length: ${(submitFormData.get('contentAr') as string).length}, Preview: ${(submitFormData.get('contentAr') as string).substring(0, 100)}` : 'EMPTY',
        contentEn: submitFormData.get('contentEn') ? `Length: ${(submitFormData.get('contentEn') as string).length}, Preview: ${(submitFormData.get('contentEn') as string).substring(0, 100)}` : 'EMPTY',
        excerptAr: submitFormData.get('excerptAr') ? `Length: ${(submitFormData.get('excerptAr') as string).length}, Preview: ${(submitFormData.get('excerptAr') as string).substring(0, 100)}` : 'EMPTY',
        excerptEn: submitFormData.get('excerptEn') ? `Length: ${(submitFormData.get('excerptEn') as string).length}, Preview: ${(submitFormData.get('excerptEn') as string).substring(0, 100)}` : 'EMPTY',
      });
      
      // Log all FormData entries
      console.log('Blog: All FormData entries:');
      const entries = Array.from(submitFormData.entries());
      entries.forEach(([key, value]) => {
        if (typeof value === 'string') {
          console.log(`  ${key}: ${value.length > 100 ? value.substring(0, 100) + '...' : value} (length: ${value.length})`);
        } else {
          console.log(`  ${key}: [File]`);
        }
      });

      let postId: number | undefined;
      if (editingPost) {
        await axiosInstance.put(api.blog.update(editingPost.id), submitFormData);
        postId = editingPost.id;
        setSuccess('تم تحديث المقال بنجاح');
      } else {
        const createRes = await axiosInstance.post(api.blog.create, submitFormData);
        postId = createRes.data?.id;
        setSuccess('تم إضافة المقال بنجاح');
      }

      // Upload extra media (images/videos) if any - use ref so we have latest list
      const filesToUpload = mediaFilesRef.current;
      if (filesToUpload.length > 0 && postId) {
        const mediaForm = new FormData();
        filesToUpload.forEach((f) => mediaForm.append('media', f));
        await axiosInstance.post(api.blog.addMedia(postId), mediaForm, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        mediaFilesRef.current = [];
        setMediaFiles([]);
      }

      mediaFilesRef.current = [];
      setMediaFiles([]);
      setShowForm(false);
      setEditingPost(null);
      setImagePreview(null);
      setFormData({
        titleAr: '',
        titleEn: '',
        slugAr: '',
        slugEn: '',
        excerptAr: '',
        excerptEn: '',
        contentAr: '',
        contentEn: '',
        image: null,
        readingTime: 1,
        isPublished: false,
      });
      fetchPosts();
    } catch (err: any) {
      console.error('Blog: Submit error:', err);
      console.error('Blog: Error response:', err.response?.data);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || `فشل ${editingPost ? 'تحديث' : 'إضافة'} المقال`;
      setError(errorMessage);
    }
  };

  const handleCancel = () => {
    mediaFilesRef.current = [];
    setMediaFiles([]);
    setShowForm(false);
    setEditingPost(null);
    setImagePreview(null);
    setFormData({
      titleAr: '',
      titleEn: '',
      slugAr: '',
      slugEn: '',
      excerptAr: '',
      excerptEn: '',
      contentAr: '',
      contentEn: '',
      image: null,
      readingTime: 1,
      isPublished: false,
    });
    setError('');
    setSuccess('');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'غير منشور';
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getImageUrl = (imagePath?: string) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${API_URL.replace('/api', '')}${imagePath}`;
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-paper)' }}>
      <nav
        style={{
          backgroundColor: '#041525',
          borderBottom: '2px solid #477CD7',
          width: '100%',
          height: '72px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between items-center h-full relative">
            <AdminBackLink />
            <h1 
              className="text-xl font-semibold text-white absolute left-1/2 transform -translate-x-1/2"
              style={{
                fontFamily: 'var(--font-arabtype)',
                letterSpacing: '0.5px',
              }}
            >
              المدونة
            </h1>
            <div className="w-24"></div>
          </div>
        </div>
      </nav>

      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          {/* Messages */}
          {!showForm && error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">
              {error}
            </div>
          )}
          {!showForm && success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg">
              {success}
            </div>
          )}

          {/* Header */}
          <div className="mb-6 flex justify-between items-center">
            <h2 className="text-2xl font-bold" style={{ color: '#ffffff' }}>قائمة المقالات</h2>
            <button
              onClick={() => {
                handleCancel();
                setShowForm(true);
              }}
              className="px-6 py-2 rounded-lg text-white transition-opacity hover:opacity-90"
              style={{
                background: 'var(--button-gradient)',
              }}
            >
              إضافة مقال جديد
            </button>
          </div>

          {/* Filters */}
          <div className="mb-6 flex gap-4 items-center">
            <label className="font-medium" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>فلترة حسب النشر:</label>
            <select
              value={filterPublished === undefined ? '' : String(filterPublished)}
              onChange={(e) => {
                setFilterPublished(e.target.value === '' ? undefined : e.target.value === 'true');
                setPage(1);
              }}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
              style={{
                borderColor: 'var(--border-main)',
              } as React.CSSProperties}
            >
              <option value="">الكل</option>
              <option value="true">منشور</option>
              <option value="false">غير منشور</option>
            </select>
            <div style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              إجمالي: {total} مقال
            </div>
          </div>

          {/* Form Modal */}
          {showForm && (
            <>
            {(error || success) && (
              <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[70] w-[min(92vw,700px)]">
                <div
                  className={`px-4 py-3 rounded-xl shadow-lg border text-sm font-medium ${
                    error
                      ? 'bg-red-50 border-red-200 text-red-800'
                      : 'bg-green-50 border-green-200 text-green-800'
                  }`}
                >
                  {error || success}
                </div>
              </div>
            )}
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {editingPost ? 'تعديل المقال' : 'إضافة مقال جديد'}
                    </h2>
                    <button
                      onClick={handleCancel}
                      className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                      ×
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Cover image */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        الصورة البارزة (غلاف المقال)
                      </label>
                      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                          <div className="w-full">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              className="block w-full text-sm text-gray-500 file:mr-3 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:cursor-pointer file:bg-blue-100 file:text-blue-800 hover:file:bg-blue-200"
                            />
                            <p className="text-xs text-gray-500 mt-2">
                              اختر صورة غلاف واضحة (PNG/JPG/WebP). الحد الافتراضي حسب السيرفر: 20MB.
                            </p>
                            {formData.image && (
                              <div className="mt-2 text-xs text-gray-600">
                                الملف المختار: <span className="font-medium">{formData.image.name}</span>{' '}
                                ({formatFileSize(formData.image.size)})
                              </div>
                            )}
                          </div>
                          {imagePreview && (
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="w-28 h-28 md:w-32 md:h-32 object-cover rounded-lg border border-gray-200"
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Extra media (images + videos) - gallery slider on post page */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        صور وفيديوهات إضافية (تعرض كسلايدر في المقال)
                      </label>
                      <p className="text-xs text-gray-500 mb-2">
                        يمكنك رفع عدة صور أو فيديوهات. تظهر مع صورة الغلاف في سلايدر أعلى المقال.
                      </p>
                      {sortedEditableMedia.length > 0 && (
                        <div className="flex flex-wrap gap-3 mb-3">
                          {sortedEditableMedia.map((m, idx) => (
                            <div
                              key={m.id}
                              draggable={!mediaSorting}
                              onDragStart={() => setDraggedMediaId(m.id)}
                              onDragOver={(e) => {
                                e.preventDefault();
                                setDragOverMediaId(m.id);
                              }}
                              onDragLeave={() => {
                                if (dragOverMediaId === m.id) setDragOverMediaId(null);
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                handleMediaDrop(m.id);
                              }}
                              onDragEnd={() => {
                                setDraggedMediaId(null);
                                setDragOverMediaId(null);
                              }}
                              className={`relative border rounded-lg overflow-hidden bg-gray-100 cursor-move ${
                                dragOverMediaId === m.id ? 'ring-2 ring-blue-500 border-blue-400' : ''
                              } ${mediaSorting ? 'opacity-70 pointer-events-none' : ''}`}
                              style={{ width: 100, height: 80 }}
                            >
                              {m.mediaType === 'video' ? (
                                <video
                                  src={`${API_URL.replace('/api', '')}${m.imageUrl}`}
                                  className="w-full h-full object-cover"
                                  muted
                                  playsInline
                                  preload="metadata"
                                />
                              ) : (
                                <img
                                  src={`${API_URL.replace('/api', '')}${m.imageUrl}`}
                                  alt=""
                                  className="w-full h-full object-cover"
                                />
                              )}
                              <button
                                type="button"
                                onClick={() => handleDeleteMedia(m.id)}
                                disabled={mediaDeleting === m.id || mediaSorting}
                                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white text-sm flex items-center justify-center hover:bg-red-600"
                              >
                                ×
                              </button>
                              <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px]">
                                #{idx + 1}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <input
                            type="file"
                            accept="image/*,video/*"
                            multiple
                            onChange={handleMediaFilesChange}
                            className="block text-sm text-gray-500 file:mr-3 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:cursor-pointer file:bg-blue-100 file:text-blue-800 hover:file:bg-blue-200"
                          />
                          {mediaFiles.length > 0 && (
                            <>
                              <span className="text-sm text-gray-700 font-medium">
                                {mediaFiles.length} ملف جاهز للرفع
                              </span>
                              {editingPost && (
                                <button
                                  type="button"
                                  onClick={handleUploadMediaNow}
                                  disabled={mediaUploading}
                                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                                >
                                  {mediaUploading ? 'جاري الرفع...' : 'رفع الملفات الآن'}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          يدعم صور + فيديو (MP4/WebM/MOV...). ترتيب الحفظ تلقائي ويبدأ من 1. الحد الافتراضي للميديا حسب السيرفر: 300MB.
                        </p>
                        {sortedEditableMedia.length > 1 && (
                          <p className="text-xs text-blue-700 mt-1">
                            اسحب وأفلت العناصر بالأعلى لتغيير الترتيب داخل السلايدر.
                          </p>
                        )}
                      </div>
                      {mediaFiles.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-3">
                          {pendingMediaPreviews.map((item) => (
                            <div
                              key={`${item.file.name}-${item.index}`}
                              draggable={!mediaUploading}
                              onDragStart={() => setDraggedPendingIndex(item.index)}
                              onDragOver={(e) => {
                                e.preventDefault();
                                setDragOverPendingIndex(item.index);
                              }}
                              onDragLeave={() => {
                                if (dragOverPendingIndex === item.index) setDragOverPendingIndex(null);
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                if (draggedPendingIndex !== null) {
                                  reorderPendingFiles(draggedPendingIndex, item.index);
                                }
                                setDraggedPendingIndex(null);
                                setDragOverPendingIndex(null);
                              }}
                              onDragEnd={() => {
                                setDraggedPendingIndex(null);
                                setDragOverPendingIndex(null);
                              }}
                              className={`rounded-lg border bg-white overflow-hidden cursor-move ${
                                dragOverPendingIndex === item.index
                                  ? 'border-blue-400 ring-2 ring-blue-500'
                                  : 'border-gray-200'
                              } ${mediaUploading ? 'opacity-70 pointer-events-none' : ''}`}
                            >
                              <div className="relative aspect-video bg-black">
                                {item.isVideo ? (
                                  <video
                                    src={item.url}
                                    className="w-full h-full object-cover"
                                    muted
                                    playsInline
                                    preload="metadata"
                                  />
                                ) : (
                                  <img
                                    src={item.url}
                                    alt={item.file.name}
                                    className="w-full h-full object-cover"
                                  />
                                )}
                                {item.isVideo && (
                                  <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <span className="w-8 h-8 rounded-full bg-black/55 text-white flex items-center justify-center text-xs">
                                      ▶
                                    </span>
                                  </span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => removePendingMedia(item.index)}
                                  disabled={mediaUploading}
                                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white text-sm flex items-center justify-center hover:bg-red-600"
                                  aria-label="حذف الملف"
                                >
                                  ×
                                </button>
                                <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px]">
                                  #{item.index + 1}
                                </span>
                              </div>
                              <div className="px-2 py-1.5">
                                <div className="text-xs font-medium text-gray-700 truncate">
                                  {item.file.name}
                                </div>
                                <div className="text-[11px] text-gray-500">
                                  {formatFileSize(item.file.size)}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {mediaFiles.length > 1 && (
                        <p className="text-xs text-blue-700 mt-2">
                          اسحب وأفلت ملفات الإضافة لتحديد ترتيبها قبل الرفع.
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          العنوان بالعربية *
                        </label>
                        <input
                          type="text"
                          value={formData.titleAr}
                          onChange={(e) => handleTitleChange('titleAr', e.target.value)}
                          required
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                          style={{
                            borderColor: 'var(--border-main)',
                          } as React.CSSProperties}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          العنوان بالإنجليزية (اختياري)
                        </label>
                        <input
                          type="text"
                          value={formData.titleEn}
                          onChange={(e) => handleTitleChange('titleEn', e.target.value)}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                          style={{
                            borderColor: 'var(--border-main)',
                          } as React.CSSProperties}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          الرابط بالعربية (Slug) *
                        </label>
                        <input
                          type="text"
                          value={formData.slugAr}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, slugAr: e.target.value }))
                          }
                          required
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                          style={{
                            borderColor: 'var(--border-main)',
                          } as React.CSSProperties}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          الرابط بالإنجليزية (Slug، اختياري)
                        </label>
                        <input
                          type="text"
                          value={formData.slugEn}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, slugEn: e.target.value }))
                          }
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                          style={{
                            borderColor: 'var(--border-main)',
                          } as React.CSSProperties}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          المحتوى بالعربية *
                        </label>
                        <RichTextEditor
                          ref={contentArRef}
                          key="contentAr"
                          value={formData.contentAr}
                          onChange={(value) => {
                            console.log('Blog: contentAr onChange called', {
                              valueLength: value ? value.length : 0,
                              valuePreview: value ? value.substring(0, 100) : 'EMPTY',
                            });
                            setFormData((prev) => {
                              const updated = { ...prev, contentAr: value };
                              console.log('Blog: contentAr updated in formData', {
                                oldLength: prev.contentAr ? prev.contentAr.length : 0,
                                newLength: updated.contentAr ? updated.contentAr.length : 0,
                              });
                              return updated;
                            });
                          }}
                          placeholder="اكتب محتوى المقال بالعربية..."
                          height="400px"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          المحتوى بالإنجليزية (اختياري)
                        </label>
                        <RichTextEditor
                          ref={contentEnRef}
                          key="contentEn"
                          value={formData.contentEn}
                          onChange={(value) => {
                            console.log('Blog: contentEn onChange called', {
                              valueLength: value ? value.length : 0,
                              valuePreview: value ? value.substring(0, 100) : 'EMPTY',
                            });
                            setFormData((prev) => {
                              const updated = { ...prev, contentEn: value };
                              console.log('Blog: contentEn updated in formData', {
                                oldLength: prev.contentEn ? prev.contentEn.length : 0,
                                newLength: updated.contentEn ? updated.contentEn.length : 0,
                              });
                              return updated;
                            });
                          }}
                          placeholder="Write article content in English..."
                          height="400px"
                          direction="ltr"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          وقت القراءة (بالدقائق)
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={formData.readingTime}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              readingTime: parseInt(e.target.value) || 1,
                            }))
                          }
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                          style={{
                            borderColor: 'var(--border-main)',
                          } as React.CSSProperties}
                        />
                      </div>

                      <div className="flex items-center">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.isPublished}
                            onChange={(e) =>
                              setFormData((prev) => ({ ...prev, isPublished: e.target.checked }))
                            }
                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="mr-2 text-sm font-medium text-gray-700">
                            نشر المقال
                          </span>
                        </label>
                      </div>
                    </div>

                    <div className="flex gap-4 justify-end pt-4">
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                      >
                        إلغاء
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2 rounded-lg text-white transition-opacity hover:opacity-90"
                        style={{
                          background: 'var(--button-gradient)',
                        }}
                      >
                        {editingPost ? 'تحديث' : 'إضافة'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            </>
          )}

          {/* Cards */}
          {loading ? (
            <div className="text-center py-12" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>جاري التحميل...</div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>لا توجد مقالات</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {posts.map((post) => {
                  const imageUrl = getImageUrl(post.image);
                  return (
                    <div
                      key={post.id}
                      className="rounded-lg overflow-hidden transition-all"
                      style={{
                        background: 'rgba(8, 42, 73, 0.6)',
                        border: '1px solid transparent',
                        color: '#ffffff',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(104, 148, 222, 0.5)';
                        e.currentTarget.style.boxShadow = '0 0 20px rgba(104, 148, 222, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'transparent';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={post.titleAr}
                          className="w-full h-48 object-cover"
                        />
                      ) : (
                        <div className="w-full h-48 flex items-center justify-center" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                          <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>لا توجد صورة</span>
                        </div>
                      )}
                      <div className="p-6">
                        <div className="mb-4">
                          <h3 className="text-lg font-semibold mb-1">{post.titleAr}</h3>
                          {post.titleEn && (
                            <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>{post.titleEn}</p>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-4 pt-4" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>وقت القراءة:</span>
                              <span className="text-sm font-medium">{post.readingTime} دقيقة</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>تاريخ النشر:</span>
                              <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>{formatDate(post.publishedAt?.toString())}</span>
                            </div>
                            <span
                              className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full w-fit ${
                                post.isPublished
                                  ? 'bg-green-500 bg-opacity-20 text-green-300'
                                  : 'bg-gray-500 bg-opacity-20 text-gray-300'
                              }`}
                            >
                              {post.isPublished ? 'منشور' : 'غير منشور'}
                            </span>
                          </div>
                          <div className="flex gap-2 items-center">
                            <button
                              onClick={() => handleEdit(post)}
                              className="p-2 rounded-lg transition-all hover:bg-blue-500 hover:bg-opacity-20"
                              style={{ color: '#477CD7' }}
                              title="تعديل"
                            >
                              {React.createElement(HiOutlinePencil as any, { size: 18 })}
                            </button>
                            <button
                              onClick={() => handleTogglePublished(post)}
                              className="p-2 rounded-lg transition-all hover:bg-yellow-500 hover:bg-opacity-20"
                              style={{ color: post.isPublished ? '#F59E0B' : '#10B981' }}
                              title={post.isPublished ? 'إلغاء النشر' : 'نشر'}
                            >
                              {post.isPublished 
                                ? React.createElement(HiOutlineEyeOff as any, { size: 18 })
                                : React.createElement(HiOutlineEye as any, { size: 18 })
                              }
                            </button>
                            <button
                              onClick={() => handleDelete(post.id)}
                              className="p-2 rounded-lg transition-all hover:bg-red-500 hover:bg-opacity-20"
                              style={{ color: '#EF4444' }}
                              title="حذف"
                            >
                              {React.createElement(HiOutlineTrash as any, { size: 18 })}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex justify-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    style={{
                      background: page === 1 ? 'rgba(255, 255, 255, 0.1)' : 'rgba(71, 124, 215, 0.2)',
                      color: '#ffffff',
                      border: '1px solid rgba(71, 124, 215, 0.3)',
                    }}
                    onMouseEnter={(e) => {
                      if (page !== 1) {
                        e.currentTarget.style.background = 'rgba(71, 124, 215, 0.3)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (page !== 1) {
                        e.currentTarget.style.background = 'rgba(71, 124, 215, 0.2)';
                      }
                    }}
                  >
                    السابق
                  </button>
                  <span className="px-4 py-2" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    صفحة {page} من {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    style={{
                      background: page === totalPages ? 'rgba(255, 255, 255, 0.1)' : 'rgba(71, 124, 215, 0.2)',
                      color: '#ffffff',
                      border: '1px solid rgba(71, 124, 215, 0.3)',
                    }}
                    onMouseEnter={(e) => {
                      if (page !== totalPages) {
                        e.currentTarget.style.background = 'rgba(71, 124, 215, 0.3)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (page !== totalPages) {
                        e.currentTarget.style.background = 'rgba(71, 124, 215, 0.2)';
                      }
                    }}
                  >
                    التالي
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Blog;

